import { useState, useCallback, useEffect } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { XMarkIcon, ArrowsPointingOutIcon } from "@heroicons/react/24/outline";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

/** Default center: Algiers, Algeria */
const DEFAULT_CENTER: [number, number] = [36.7538, 3.0588];
const DEFAULT_ZOOM = 10;

export interface LocationValue {
  latitude?: number | null;
  longitude?: number | null;
  location_note?: string | null;
}

interface LocationPickerProps {
  value: LocationValue;
  onChange: (value: LocationValue) => void;
}

/** Default marker icon served by local app bundle (avoids CDN/CSP issues). */
const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

function MapClickHandler({
  onLocationSelect,
}: {
  onLocationSelect: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MapSyncView({ center }: { center: [number, number] }) {
  const map = useMap();
  useEffect(() => {
    // Keep map correctly sized and centered when opened inside dialogs/expanders.
    map.invalidateSize();
    map.setView(center);
  }, [map, center]);
  return null;
}

function MapContent({
  center,
  lat,
  lng,
  onMapClick,
  value,
  onChange,
}: {
  center: [number, number];
  lat: number | null;
  lng: number | null;
  onMapClick: (lat: number, lng: number) => void;
  value: LocationValue;
  onChange: (value: LocationValue) => void;
}) {
  return (
    <>
      <MapSyncView center={center} />
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <MapClickHandler onLocationSelect={onMapClick} />
      {lat != null && lng != null && (
        <Marker
          position={[lat, lng]}
          draggable
          eventHandlers={{
            dragend: (e: { target: L.Marker }) => {
              const pos = e.target.getLatLng();
              onChange({ ...value, latitude: pos.lat, longitude: pos.lng });
            },
          }}
        />
      )}
    </>
  );
}

const INLINE_MAP_HEIGHT_CLASS = "h-64"; // 256px
const EXPANDED_MAP_HEIGHT_CLASS = "h-[70vh]";

export function LocationPicker({ value, onChange }: LocationPickerProps) {
  const [geoError, setGeoError] = useState<string | null>(null);
  const [isLoadingGeo, setIsLoadingGeo] = useState(false);
  const [isMapExpanded, setIsMapExpanded] = useState(false);

  const lat = value.latitude ?? null;
  const lng = value.longitude ?? null;
  const center: [number, number] = lat != null && lng != null ? [lat, lng] : DEFAULT_CENTER;

  const handleUseCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation is not supported by your browser");
      return;
    }
    setIsLoadingGeo(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        onChange({
          ...value,
          latitude,
          longitude,
        });
        setIsLoadingGeo(false);
      },
      (err) => {
        if (err.code === 1) {
          setGeoError("Location permission denied");
        } else {
          setGeoError(err.message || "Could not get location");
        }
        setIsLoadingGeo(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [value, onChange]);

  const handleMapClick = useCallback(
    (lat: number, lng: number) => {
      onChange({
        ...value,
        latitude: lat,
        longitude: lng,
      });
    },
    [value, onChange]
  );

  const handleLatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.trim();
    if (v === "") {
      onChange({ ...value, latitude: null });
      return;
    }
    const num = parseFloat(v);
    if (!isNaN(num) && num >= -90 && num <= 90) {
      onChange({ ...value, latitude: num });
    }
  };

  const handleLngChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.trim();
    if (v === "") {
      onChange({ ...value, longitude: null });
      return;
    }
    const num = parseFloat(v);
    if (!isNaN(num) && num >= -180 && num <= 180) {
      onChange({ ...value, longitude: num });
    }
  };

  const handleNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Preserve spaces while typing; trim only when payload is submitted.
    onChange({ ...value, location_note: e.target.value });
  };

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={handleUseCurrentLocation}
        disabled={isLoadingGeo}
        className="px-4 py-2 bg-neutral-100 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-600 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoadingGeo ? "Getting location..." : "📍 Use Current Location"}
      </button>
      {geoError && <p className="text-sm text-red-600 dark:text-red-400">{geoError}</p>}

      <div className="space-y-2">
        <div
          className={`rounded-lg border border-neutral-300 dark:border-neutral-600 overflow-hidden ${INLINE_MAP_HEIGHT_CLASS}`}
        >
          <MapContainer
            center={center}
            zoom={DEFAULT_ZOOM}
            className="h-full w-full"
            scrollWheelZoom={true}
          >
            <MapContent
              center={center}
              lat={lat}
              lng={lng}
              onMapClick={handleMapClick}
              value={value}
              onChange={onChange}
            />
          </MapContainer>
        </div>
        <button
          type="button"
          onClick={() => setIsMapExpanded(true)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
        >
          <ArrowsPointingOutIcon className="h-4 w-4" aria-hidden />
          Expand map
        </button>
      </div>

      <Dialog
        open={isMapExpanded}
        onClose={setIsMapExpanded}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="mx-auto w-full max-w-4xl rounded-xl bg-white dark:bg-neutral-800 shadow-xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-600">
              <DialogTitle className="text-lg font-medium text-neutral-900 dark:text-neutral-100">
                Select location on map
              </DialogTitle>
              <button
                type="button"
                onClick={() => setIsMapExpanded(false)}
                className="p-1.5 rounded-lg text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className={`w-full ${EXPANDED_MAP_HEIGHT_CLASS}`}>
              <MapContainer
                center={center}
                zoom={DEFAULT_ZOOM}
                className="h-full w-full rounded-b-xl"
                scrollWheelZoom={true}
              >
                <MapContent
                  center={center}
                  lat={lat}
                  lng={lng}
                  onMapClick={handleMapClick}
                  value={value}
                  onChange={onChange}
                />
              </MapContainer>
            </div>
            <div className="px-4 py-3 border-t border-neutral-200 dark:border-neutral-600 flex justify-end">
              <button
                type="button"
                onClick={() => setIsMapExpanded(false)}
                className="px-4 py-2 bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 rounded-lg hover:bg-neutral-800 dark:hover:bg-neutral-200 font-medium"
              >
                Done
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
            Latitude
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={lat ?? ""}
            onChange={handleLatChange}
            placeholder="e.g. 36.7538"
            className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
            Longitude
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={lng ?? ""}
            onChange={handleLngChange}
            placeholder="e.g. 3.0588"
            className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
          Location note
        </label>
        <input
          type="text"
          value={value.location_note ?? ""}
          onChange={handleNoteChange}
          placeholder="e.g. Blue gate, 2nd floor"
          maxLength={255}
          className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
        />
      </div>
    </div>
  );
}
